import { useState, useEffect } from 'react';
import axios from 'axios';
import { AlertTriangle, Shield, TrendingUp, Activity, ChevronDown, ChevronUp } from 'lucide-react';
import { BACKEND_URL } from '../config/api';

const API = `${BACKEND_URL}/api`;

export default function InjuryRiskPredictor({ childId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/injury-risk/predict/${childId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setData(res.data);
      } catch { /* silent */ }
      setLoading(false);
    };
    fetch();
  }, [childId]);

  if (loading) return <div className="animate-pulse h-32 bg-zinc-900/50 rounded-xl" />;
  if (!data || !data.risks?.length) return null;

  const riskColor = { high: 'text-red-400', moderate: 'text-amber-400', low: 'text-green-400' };
  const riskBg = { high: 'bg-red-500/10 border-red-500/30', moderate: 'bg-amber-500/10 border-amber-500/30', low: 'bg-green-500/10 border-green-500/30' };

  const topRisks = expanded ? data.risks : data.risks.slice(0, 2);

  return (
    <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden" data-testid="injury-risk-predictor">
      <div 
        className="p-5 flex items-center justify-between cursor-pointer hover:bg-zinc-800/30 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
            data.overall_risk === 'high' ? 'bg-red-500/20' : data.overall_risk === 'moderate' ? 'bg-amber-500/20' : 'bg-green-500/20'
          }`}>
            <Shield className={`h-5 w-5 ${riskColor[data.overall_risk] || 'text-zinc-400'}`} />
          </div>
          <div>
            <h3 className="text-sm font-bold text-white uppercase tracking-wider">Injury Risk Assessment</h3>
            <p className={`text-xs font-medium ${riskColor[data.overall_risk] || 'text-zinc-400'}`}>
              Overall: {data.overall_risk?.toUpperCase()} RISK
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {data.growth_data?.velocity_cm_per_year && (
            <div className="text-right hidden sm:block">
              <p className="text-xs text-zinc-500">Growth Rate</p>
              <p className={`text-sm font-bold ${data.growth_data.is_rapid_growth ? 'text-amber-400' : 'text-green-400'}`}>
                {data.growth_data.velocity_cm_per_year} cm/yr
              </p>
            </div>
          )}
          {expanded ? <ChevronUp className="h-4 w-4 text-zinc-500" /> : <ChevronDown className="h-4 w-4 text-zinc-500" />}
        </div>
      </div>

      {expanded && (
        <div className="border-t border-zinc-800">
          {/* Growth & PHV Summary */}
          <div className="px-5 py-3 grid grid-cols-3 gap-4 bg-zinc-900/30">
            <div className="text-center">
              <p className="text-[10px] text-zinc-500 uppercase tracking-wider">PHV Status</p>
              <p className="text-xs font-semibold text-white mt-0.5">{data.growth_data?.phv_status?.replace('_', ' ') || 'Unknown'}</p>
            </div>
            <div className="text-center">
              <p className="text-[10px] text-zinc-500 uppercase tracking-wider">Weekly Sessions</p>
              <p className="text-xs font-semibold text-white mt-0.5">{data.training_data?.weekly_sessions || 0}</p>
            </div>
            <div className="text-center">
              <p className="text-[10px] text-zinc-500 uppercase tracking-wider">Load Trend</p>
              <p className={`text-xs font-semibold mt-0.5 ${
                data.training_data?.load_trend === 'increasing' ? 'text-amber-400' : 'text-green-400'
              }`}>{data.training_data?.load_trend || 'N/A'}</p>
            </div>
          </div>

          {/* Risk Cards */}
          <div className="p-4 space-y-3">
            {topRisks.map((risk) => (
              <div key={risk.injury_type} className={`border rounded-lg p-4 ${riskBg[risk.risk_level]}`}>
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h4 className="text-sm font-bold text-white">{risk.name}</h4>
                    <p className="text-xs text-zinc-400 mt-0.5">{risk.description}</p>
                  </div>
                  <div className="flex items-center gap-1.5 ml-3 flex-shrink-0">
                    <div className="w-16 h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                      <div 
                        className={`h-full rounded-full ${
                          risk.risk_level === 'high' ? 'bg-red-500' : risk.risk_level === 'moderate' ? 'bg-amber-500' : 'bg-green-500'
                        }`} 
                        style={{ width: `${(risk.risk_score / risk.max_score) * 100}%` }} 
                      />
                    </div>
                    <span className={`text-xs font-bold ${riskColor[risk.risk_level]}`}>{risk.risk_score}/{risk.max_score}</span>
                  </div>
                </div>
                
                {risk.contributing_factors?.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {risk.contributing_factors.map((f, i) => (
                      <div key={i} className="flex items-start gap-2">
                        <AlertTriangle className="h-3 w-3 text-amber-400 mt-0.5 flex-shrink-0" />
                        <p className="text-xs text-zinc-300">{f}</p>
                      </div>
                    ))}
                  </div>
                )}

                {risk.prevention_tips?.length > 0 && (
                  <div className="mt-3 pt-2 border-t border-zinc-700/50">
                    <p className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1.5">Prevention</p>
                    <div className="flex flex-wrap gap-1.5">
                      {risk.prevention_tips.map((tip, i) => (
                        <span key={i} className="text-[11px] px-2 py-0.5 bg-zinc-800/80 text-zinc-300 rounded-full">{tip}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
